import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, debounceTime, map } from 'rxjs/operators';
import { merge, Subscription } from 'rxjs';

import { CustomerChatbotService } from './types';
import { AzavistaApiService, ITokenPayload, IUser } from '@azavista/servicelib';
import { environment } from 'src/environments/environment';
import { routeToView, ViewNavigatorService } from './view-navigator';
import { AuthTokens, AzavistaAIAssistant } from './azavista-ai-chatbot.types';
import { ViewHostContextService } from './view-host-context.service';
import { AzavistaGlobalMatTabEventService, GlobalButtonEventService } from '@azavista/components/shared';

const WIDGET_SRC = `https://devk8s.storage.googleapis.com/asset/86/69f993893f83649f29ad3f35/azavista-ai-assistant.js`;
const WIDGET_TAG = 'azavista-ai-assistant';

const DEV_SECURITY_HEADERS: HeadersInit = {
  'X-Aza-Security-Context': JSON.stringify({
    id: 123,
    customer_id: 456,
    email: 'user@example.com',
    azavista_2_permissions: ['ai.chat'],
  }),
};

function isLocalAiBaseUrl(url: string | undefined): boolean {
  return !!url && /^(https?:\/\/)?localhost\b/i.test(url);
}

@Injectable({ providedIn: 'root' })
export class AzavistaAIChatbotService extends CustomerChatbotService {
  private apiSvc = inject(AzavistaApiService);
  private router = inject(Router);
  private viewNav = inject(ViewNavigatorService);
  private viewHostContext = inject(ViewHostContextService);
  private tabEventService = inject(AzavistaGlobalMatTabEventService);
  private buttonEventService = inject(GlobalButtonEventService);

  private scriptPromise?: Promise<void>;
  private routeSub?: Subscription;

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor() {
    super();
  }

  async showChatbot() {
    if (this.currentChatbotUserState === 'unauthenticated') return;
    const widget = await this.ensureWidget();
    widget.style.removeProperty('display');
    widget.open();
  }
  async hideChatbot() {
    const widget = this.getWidget();
    if (!widget) return;
    widget.close();
    widget.style.display = 'none';
  }
  async initUserSession(_user: IUser, _token: ITokenPayload): Promise<void> {
    await this.ensureWidget();
  }
  async endUserSession(): Promise<void> {
    this.routeSub?.unsubscribe();
    this.routeSub = undefined;
    const widget = this.getWidget();
    if (widget) {
      widget.close();
      widget.remove();
    }
  }
  async initUnAuthenticatedUser() {
    // The AI backend rejects anonymous callers, and mounting the widget fires
    // an immediate thread-list fetch — creating it here just produces one
    // guaranteed tokenless 401 on page load. Wait for initUserSession.
  }

  private async ensureWidget(): Promise<AzavistaAIAssistant> {
    await this.ensureScript();
    let widget = this.getWidget();
    if (!widget) {
      widget = document.createElement(WIDGET_TAG) as AzavistaAIAssistant;
      widget.setAttribute('base-url', environment.aiBaseUrl || environment.baseUrl.replace('/3.0',''));
      widget.hostAdapter = {
        getHeaders: async () => {
            if (isLocalAiBaseUrl(environment.aiBaseUrl)) {
                return DEV_SECURITY_HEADERS;
            }
            const authHeader = await this.resolveAuthorizationHeader();
            return authHeader ? { Authorization: authHeader } : {};
        },
        getTokens: async () => this.resolveTokens(),
        handleAction: async action => {
            await this.viewNav.navigate(action);
        },
     };
      document.body.appendChild(widget);
    }
    await customElements.whenDefined(WIDGET_TAG);
    this.startContextSync(widget);
    return widget;
  }

  private startContextSync(widget: AzavistaAIAssistant): void {
    if (this.routeSub || !widget.setContext) return;
    this.pushContext(widget, this.router.url);
    this.routeSub = merge(
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(e => e.urlAfterRedirects)
      ),
      this.tabEventService.tabChangeEvent$.pipe(
        map(() => `${window.location.pathname}${window.location.search}${window.location.hash}`)
      ),
      this.buttonEventService.buttonClicked$.pipe(
        map(event => `${window.location.pathname}${window.location.search}#${event.urlHash}`)
      )
    )
      .pipe(debounceTime(500))
      .subscribe(path => this.pushContext(widget, path));
  }

  private pushContext(widget: AzavistaAIAssistant, pathWithoutDomain: string): void {
    const view = routeToView(pathWithoutDomain) ?? undefined;
    this.viewHostContext.setView(view, widget);
  }

  private getWidget(): AzavistaAIAssistant | null {
    return document.querySelector<AzavistaAIAssistant>(WIDGET_TAG);
  }
  private async resolveTokens(): Promise<AuthTokens> {
    // getAuthorizationHeaderValue refreshes the access token if expired; the
    // refresh token lives in the servicelib auth response in localStorage
    // (same source app.component.ts reads).
    const authHeader = await this.resolveAuthorizationHeader();
    const accessToken = authHeader.replace(/^Bearer\s+/i, '');
    let refreshToken: string | undefined;
    try {
      refreshToken = JSON.parse(
        localStorage.getItem('azavista.apiService.authResponse') ?? 'null'
      )?.refresh_token;
    } catch {
      refreshToken = undefined;
    }
    return {
      ...(accessToken ? { access_token: accessToken } : {}),
      ...(refreshToken ? { refresh_token: refreshToken } : {}),
    };
  }

  private async resolveAuthorizationHeader(): Promise<string> {
    // No isAuthenticated() gate: it reads authorizationHeaderValue, which
    // servicelib blanks for the duration of a token refresh — so a valid
    // session reports "logged out" mid-refresh and the request goes out bare.
    // getAuthorizationHeaderValue awaits that refresh, and still returns ''
    // for a genuinely anonymous user (no accessTokenInfo to refresh).
    try {
      return (await this.apiSvc.getAuthorizationHeaderValue()) ?? '';
    } catch {
      return '';
    }
  }
  private ensureScript(): Promise<void> {
    if (customElements.get(WIDGET_TAG)) {
      return Promise.resolve();
    }
    if (this.scriptPromise) {
      return this.scriptPromise;
    }
    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${WIDGET_SRC}"]`
      );
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = WIDGET_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${WIDGET_SRC}`));
      document.head.appendChild(script);
    });
    return this.scriptPromise;
  }
}
